import { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../firebase';
import {
  updateDoc,
  doc,
  writeBatch,
  collection,
  onSnapshot,
  getDocs,
  query,
  where,
  orderBy,
  setDoc
} from 'firebase/firestore';
import type { Website } from '../types';
import { auth } from '../firebase'; // Added import for auth
import { onAuthStateChanged } from 'firebase/auth';
import { apiClient } from '../api/client';
import { checksCache, cacheKeys } from '../utils/cache';
import type { UpdateWebsiteRequest } from '../api/types';
import { normalizeFolder, folderHasPrefix, replaceFolderPrefix, getFolderPathError } from '../lib/folder-utils';

// Development flag for debug logging
const DEBUG_MODE = import.meta.env.DEV && import.meta.env.VITE_DEBUG_CHECKS === 'true';

// Sparse orderIndex constants - reduces Firestore writes by ~98%
// Instead of sequential indices (0,1,2,3...), use large gaps (1000,2000,3000...)
// This allows inserting between items without reindexing all items
const ORDER_INDEX_GAP = 1000;

// Debounce delay for folder updates (ms)
const FOLDER_DEBOUNCE_DELAY = 300;

export interface UseChecksOptions {
  /** If false, fetches checks once instead of subscribing to real-time updates. Default: true */
  realtime?: boolean;
  /** Callback for status changes (only works with realtime: true) */
  onStatusChange?: (name: string, previousStatus: string, newStatus: string) => void;
}

export function useChecks(
  userId: string | null, 
  log: (msg: string) => void,
  optionsOrOnStatusChange?: UseChecksOptions | ((name: string, previousStatus: string, newStatus: string) => void)
) {
  // Support both old signature (onStatusChange callback) and new signature (options object)
  const options: UseChecksOptions = typeof optionsOrOnStatusChange === 'function'
    ? { onStatusChange: optionsOrOnStatusChange, realtime: true }
    : { realtime: true, ...optionsOrOnStatusChange };
  
  const { realtime = true, onStatusChange } = options;

  // Stable refs so callbacks never destabilize useCallback/useEffect deps
  const logRef = useRef(log);
  useEffect(() => { logRef.current = log; });
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; });

  const [checks, setChecks] = useState<Website[]>([]);
  const [loading, setLoading] = useState(true);
  const [firebaseUid, setFirebaseUid] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    if (typeof document === 'undefined') return true;
    return document.visibilityState === 'visible';
  });
  const previousStatuses = useRef<Record<string, string>>({});
  const hasLoadedOnceRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const optimisticUpdatesRef = useRef<Set<string>>(new Set()); // Track optimistic updates
  const manualChecksInProgressRef = useRef<Set<string>>(new Set()); // Track manual checks in progress
  const folderUpdatesRef = useRef<Set<string>>(new Set()); // Track folder-only updates (don't pulse rows)
  const isDraggingRef = useRef(false); // Guard: prevent onSnapshot from overwriting mid-drag state
  const pendingSnapshotRef = useRef<Website[] | null>(null); // Buffer Firestore data received during drag
  const pendingDeletesRef = useRef<Set<string>>(new Set()); // Track IDs being deleted so onSnapshot won't re-add them
  const pendingAddsRef = useRef<Set<string>>(new Set()); // Track temp IDs being added so onSnapshot won't discard them
  
  // Debounced folder updates - batches rapid folder changes into single write
  const pendingFolderUpdatesRef = useRef<Map<string, string | null>>(new Map());
  const folderDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Wait for Firebase auth to be ready (similar to useUserNotifications pattern)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUid(user?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleVisibility = () => {
      setIsVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Fetch checks once (non-realtime mode)
  const fetchChecksOnce = useCallback(async () => {
    // Query by the account id the caller passed (the owner's uid while a
    // teammate is inside a team account), once Firebase auth is ready.
    const uid = firebaseUid ? userId : null;
    if (!uid) {
      setChecks([]);
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'checks'),
        where('userId', '==', uid),
        orderBy('orderIndex', 'asc')
      );

      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map((d) => {
        const data = d.data() as Website;
        return { ...data, id: d.id };
      });

      hasLoadedOnceRef.current = true;
      setChecks(docs);
      setLoading(false);
    } catch (error) {
      console.error('[useChecks] Failed to fetch checks:', error);
      logRef.current('Failed to fetch checks: ' + (error as Error).message);
      setLoading(false);
    }
  }, [firebaseUid, userId]);

  // Real-time subscription to checks using Firestore onSnapshot
  const subscribeToChecks = useCallback(() => {
    // Wait for Firebase auth before subscribing, then query by the account id
    // the caller passed (the owner's uid while inside a team account).
    const uid = firebaseUid ? userId : null;
    if (!uid) {
      setChecks([]);
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'checks'),
        where('userId', '==', uid),
        orderBy('orderIndex', 'asc')
      );

      unsubscribeRef.current = onSnapshot(
        q,
        (snapshot) => {
          const docs = snapshot.docs.map((d) => {
            const data = d.data() as Website;
            return { ...data, id: d.id };
          });

          // Track status changes for notifications
          docs.forEach((check) => {
            const previousStatus = previousStatuses.current[check.id];
            const currentStatus = check.status || 'unknown';

            if (previousStatus && previousStatus !== currentStatus) {
              if (DEBUG_MODE) {
                logRef.current(`Status change: ${check.name} went from ${previousStatus} to ${currentStatus}`);
              }
              onStatusChangeRef.current?.(check.name, previousStatus, currentStatus);
            }
            previousStatuses.current[check.id] = currentStatus;
          });

          hasLoadedOnceRef.current = true;

          // Filter out checks that are being optimistically deleted so
          // onSnapshot doesn't re-add them before the server confirms.
          let merged: Website[] = pendingDeletesRef.current.size > 0
            ? docs.filter(d => !pendingDeletesRef.current.has(d.id))
            : docs;

          // During an active drag, buffer Firestore data instead of overwriting
          // the user's local reordered state. The buffered data will be applied
          // after commit/rollback.
          if (isDraggingRef.current) {
            pendingSnapshotRef.current = merged;
          } else if (pendingAddsRef.current.size > 0) {
            // Preserve optimistic adds (temp IDs) that aren't in the snapshot yet.
            setChecks(prev => {
              const optimisticAdds = prev.filter(c => pendingAddsRef.current.has(c.id));
              return [...merged, ...optimisticAdds];
            });
          } else {
            setChecks(merged);
          }
          setLoading(false);
        },
        (error) => {
          console.error('[useChecks] onSnapshot error:', error);
          logRef.current('Realtime subscription error: ' + (error as Error).message);
          setLoading(false);
        }
      );
    } catch (error) {
      console.error('[useChecks] Failed to subscribe to checks:', error);
      logRef.current('Failed to subscribe to checks: ' + (error as Error).message);
      setLoading(false);
    }
  }, [firebaseUid, userId]);

  useEffect(() => {
    if (!userId) {
      // If no Clerk userId, clear checks and stop loading
      setChecks([]);
      setLoading(false);
      hasLoadedOnceRef.current = false;
      return;
    }

    // Only log debug info in development mode
    if (DEBUG_MODE) {
      console.log('[useChecks] User ID:', userId);
      console.log('[useChecks] Firebase auth current user:', auth.currentUser?.uid);
      console.log('[useChecks] Firebase auth state:', auth.currentUser ? 'authenticated' : 'not authenticated');

      if (auth.currentUser && auth.currentUser.uid !== userId) {
        console.warn('[useChecks] User ID mismatch! Clerk:', userId, 'Firebase:', auth.currentUser.uid);
        logRef.current('Warning: User ID mismatch detected. This may cause permission issues.');
      }
    }

    // Only subscribe when Firebase auth is ready
    if (!firebaseUid) {
      if (!hasLoadedOnceRef.current) {
        setLoading(true);
      }
      return;
    }

    // Non-realtime mode: fetch once and don't subscribe
    if (!realtime) {
      if (!hasLoadedOnceRef.current) {
        setLoading(true);
        fetchChecksOnce();
      }
      return;
    }

    // Realtime mode: subscribe to changes
    if (!isVisible) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      setLoading(false);
      return;
    }

    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    subscribeToChecks();

    // Cleanup subscription
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [userId, firebaseUid, isVisible, realtime, subscribeToChecks, fetchChecksOnce]);

  // Invalidate cache when checks are modified
  const invalidateCache = useCallback(() => {
    if (userId) {
      checksCache.delete(cacheKeys.checks(userId));
    }
  }, [userId]);



  // Direct Firestore operations with optimistic updates
  const addCheck = useCallback(async (name: string, url: string) => {
    if (!userId) throw new Error('Authentication required');
    
    // Enhanced URL validation
    if (!url.trim().match(/^https?:\/\/.+/)) {
      throw new Error("Invalid URL format. Must start with http:// or https://");
    }
    
    // Check URL length
    if (url.length < 10) {
      throw new Error("URL too short (minimum 10 characters)");
    }
    
    if (url.length > 2048) {
      throw new Error("URL too long (maximum 2048 characters)");
    }
    
    // Check for blocked domains
    const blockedDomains = ['localhost', '127.0.0.1', '0.0.0.0', '::1', 'example.com', 'test.com', 'invalid.com'];
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      
      const isBlocked = blockedDomains.some(blocked => 
        hostname === blocked || hostname.endsWith(`.${blocked}`)
      );
      
      if (isBlocked) {
        throw new Error("This domain is not allowed for monitoring");
      }
      
      // Check for local addresses
      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        throw new Error("Local addresses are not allowed");
      }
      
    } catch (error) {
      if (error instanceof Error && error.message.includes("domain")) {
        throw error;
      }
      throw new Error("Invalid URL format");
    }
    
    // Allow duplicate URLs so users can monitor variants (http/https, www, paths, subdomains).
    
    const now = Date.now();
    
    // Calculate sparse orderIndex - add to bottom with large gap
    // This avoids updating ALL existing checks (reduces N+1 writes to just 1 write)
    const existingOrderIndexes = checks
      .map(c => c.orderIndex)
      .filter((idx): idx is number => typeof idx === 'number' && !isNaN(idx));
    const maxOrderIndex = existingOrderIndexes.length > 0 
      ? Math.max(...existingOrderIndexes) 
      : 0;
    const newOrderIndex = maxOrderIndex + ORDER_INDEX_GAP;
    
    // Create optimistic check data
    const optimisticCheck: Website = {
      id: `temp_${Date.now()}`, // Temporary ID
      url: url.trim(),
      name: (name || url).trim(),
      userId,
      createdAt: now,
      updatedAt: now,
      status: "unknown" as const,
      downtimeCount: 0,
      lastChecked: 0,
      orderIndex: newOrderIndex, // Add to bottom with sparse index
      lastDowntime: null,
      checkFrequency: 5, // Default 5 minutes — best interval the free tier allows
      consecutiveFailures: 0,
      consecutiveSuccesses: 0,
      userTier: 'free' as const,
      type: 'website' as const,
    };

    // Optimistically add to local state (at end since we're using bottom placement)
    setChecks(prevChecks => [...prevChecks, optimisticCheck]);
    optimisticUpdatesRef.current.add(optimisticCheck.id);
    pendingAddsRef.current.add(optimisticCheck.id);

    try {
      // Ensure all required fields are present and match Firestore rules exactly
      const checkData = {
        url: url.trim(),
        name: (name || url).trim(),
        userId,
        createdAt: now,
        updatedAt: now,
        status: "unknown" as const,
        downtimeCount: 0,
        lastChecked: 0,
        nextCheckAt: now, // Check immediately on next scheduler run
        orderIndex: newOrderIndex, // Add to bottom with sparse index
        lastDowntime: null,
        // Required fields for cost optimization
        checkFrequency: 5, // Default 5 minutes — best interval the free tier allows
        consecutiveFailures: 0,
        userTier: 'free' as const, // Default to free tier
        // Default type for backward compatibility
        type: 'website' as const,
      };
      
      // Validate data before sending to Firestore
      if (!checkData.url.match(/^https?:\/\/.+/)) {
        throw new Error("Invalid URL format. Must start with http:// or https://");
      }
      
      if (checkData.name.length < 2 || checkData.name.length > 200) {
        throw new Error("Name must be between 2 and 200 characters");
      }
      
      // Single document write - no need to update existing checks' orderIndex!
      // This reduces writes from N+1 to just 1
      const newCheckRef = doc(collection(db, 'checks'));
      await setDoc(newCheckRef, checkData);
      
      // Remove from optimistic updates and invalidate cache
      optimisticUpdatesRef.current.delete(optimisticCheck.id);
      pendingAddsRef.current.delete(optimisticCheck.id);
      invalidateCache();

      // Update the optimistic check with the real ID
      setChecks(prevChecks =>
        prevChecks.map(check =>
          check.id === optimisticCheck.id
            ? { ...check, id: newCheckRef.id }
            : check
        )
      );
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(prevChecks => prevChecks.filter(check => check.id !== optimisticCheck.id));
      optimisticUpdatesRef.current.delete(optimisticCheck.id);
      pendingAddsRef.current.delete(optimisticCheck.id);
      
      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot add check. Check user authentication and Firestore rules.');
      } else {
        log('Error adding check: ' + error.message);
      }
      throw error; // Re-throw to be caught by the caller
    }
  }, [userId, checks, invalidateCache, log]);

  const updateCheck = useCallback(async (request: UpdateWebsiteRequest) => {
    if (!userId) throw new Error('Authentication required');

    const {
      id,
      name,
      url,
      checkFrequency,
      responseTimeLimit,
      immediateRecheckEnabled,
      downConfirmationAttempts,
      type,
      httpMethod,
      expectedStatusCodes,
      requestHeaders,
      requestBody,
      responseValidation,
      redirectValidation,
      cacheControlNoCache,
      ignoreSslErrors,
      timezone,
      checkRegionOverride,
      severity,
      public: isPublic,
      publicSlug,
    } = request;

    // Check if check exists and belongs to user
    const check = checks.find(w => w.id === id);
    if (!check) {
      throw new Error("Check not found");
    }
    
    const targetType = type ?? (check.type === 'rest_endpoint' ? 'rest_endpoint' : check.type === 'llm' ? 'llm' : check.type === 'tcp' ? 'tcp' : check.type === 'udp' ? 'udp' : check.type === 'ping' ? 'ping' : check.type === 'websocket' ? 'websocket' : check.type === 'redirect' ? 'redirect' : check.type === 'dns' ? 'dns' : check.type === 'heartbeat' ? 'heartbeat' : 'website');

    // Allow duplicate URLs so users can monitor variants (http/https, www, paths, subdomains).

    // Validate data before sending to Firestore
    const isSocketType = targetType === 'tcp' || targetType === 'udp';
    const isPingType = targetType === 'ping';
    const isWebSocketType = targetType === 'websocket';
    const isDnsType = targetType === 'dns';
    const isHeartbeatType = targetType === 'heartbeat';
    if (!isHeartbeatType) {
      const urlPattern = isDnsType ? /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/ : isPingType ? /^ping:\/\/.+/ : isSocketType ? /^(tcp|udp):\/\/.+:\d+/ : isWebSocketType ? /^wss?:\/\/.+/ : /^https?:\/\/.+/;
      if (!url.trim().match(urlPattern)) {
        throw new Error(isDnsType ? "Invalid domain format. Use a bare domain like example.com" : isPingType ? "Invalid target format. Must be ping://hostname" : isSocketType ? "Invalid target format. Must be tcp://host:port or udp://host:port" : isWebSocketType ? "Invalid WebSocket URL. Must start with ws:// or wss://" : "Invalid URL format. Must start with http:// or https://");
      }
    }
    
    const trimmedName = (name || url).trim();
    if (trimmedName.length < 2 || trimmedName.length > 200) {
      throw new Error("Name must be between 2 and 200 characters");
    }
    
    // Store original values for rollback
    const originalCheck = { ...check };
    
    // Optimistically update local state
    setChecks(prevChecks => 
      prevChecks.map(c => 
        c.id === id 
          ? { 
              ...c, 
              name: trimmedName, 
              url: url.trim(), 
              checkFrequency: checkFrequency ?? c.checkFrequency ?? 60,
              responseTimeLimit: responseTimeLimit === null ? undefined : (responseTimeLimit !== undefined ? responseTimeLimit : c.responseTimeLimit),
              immediateRecheckEnabled: immediateRecheckEnabled !== undefined ? immediateRecheckEnabled : c.immediateRecheckEnabled,
              downConfirmationAttempts: downConfirmationAttempts !== undefined ? downConfirmationAttempts : c.downConfirmationAttempts,
              type: targetType,
              httpMethod: httpMethod !== undefined ? httpMethod : c.httpMethod,
              expectedStatusCodes: expectedStatusCodes !== undefined ? expectedStatusCodes : c.expectedStatusCodes,
              requestHeaders: requestHeaders !== undefined ? requestHeaders : c.requestHeaders,
              requestBody: requestBody !== undefined ? requestBody : c.requestBody,
              responseValidation: responseValidation !== undefined ? responseValidation : c.responseValidation,
              redirectValidation: redirectValidation !== undefined ? (redirectValidation ?? undefined) : c.redirectValidation,
              cacheControlNoCache: cacheControlNoCache !== undefined ? cacheControlNoCache : c.cacheControlNoCache,
              ignoreSslErrors: ignoreSslErrors !== undefined ? ignoreSslErrors : c.ignoreSslErrors,
              timezone: timezone !== undefined ? (timezone ?? undefined) : c.timezone,
              checkRegionOverride: checkRegionOverride !== undefined ? checkRegionOverride : c.checkRegionOverride,
              severity: severity !== undefined ? severity : c.severity,
              public: isPublic !== undefined ? isPublic : c.public,
              publicSlug: publicSlug !== undefined ? (publicSlug ?? undefined) : c.publicSlug,
              updatedAt: Date.now(),
              lastChecked: 0 // Force re-check on next scheduled run
            }
          : c
      )
    );
    optimisticUpdatesRef.current.add(id);
    
    try {
      await apiClient.updateWebsite({
        id,
        url: url.trim(),
        name: trimmedName,
        checkFrequency: checkFrequency ?? check.checkFrequency ?? 60,
        responseTimeLimit,
        immediateRecheckEnabled: immediateRecheckEnabled !== undefined ? immediateRecheckEnabled : check.immediateRecheckEnabled,
        downConfirmationAttempts: downConfirmationAttempts !== undefined ? downConfirmationAttempts : check.downConfirmationAttempts,
        type: targetType,
        httpMethod,
        expectedStatusCodes,
        requestHeaders,
        requestBody,
        responseValidation,
        redirectValidation: redirectValidation ?? undefined,
        cacheControlNoCache,
        ignoreSslErrors,
        timezone,
        checkRegionOverride,
        severity,
        public: isPublic,
        publicSlug,
      });

      // Remove from optimistic updates and invalidate cache
      optimisticUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(prevChecks => 
        prevChecks.map(c => c.id === id ? originalCheck : c)
      );
      optimisticUpdatesRef.current.delete(id);
      
      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot update check. Check user authentication and Firestore rules.');
      } else {
        log('Error updating check: ' + error.message);
      }
      throw error; // Re-throw to be caught by the caller
    }
  }, [userId, checks, invalidateCache, log]);

  // Folder feature: set / clear folder (group) for a check
  const setCheckFolder = useCallback(async (id: string, folder: string | null) => {
    if (!userId) throw new Error('Authentication required');

    const check = checks.find(w => w.id === id);
    if (!check) {
      throw new Error("Check not found");
    }

    const normalizedFolder = normalizeFolder(folder);
    const folderError = getFolderPathError(normalizedFolder);
    if (folderError) throw new Error(folderError);

    const originalCheck = { ...check };
    const now = Date.now();

    // Optimistic local update
    setChecks(prevChecks =>
      prevChecks.map(c =>
        c.id === id
          ? {
              ...c,
              folder: normalizedFolder,
              updatedAt: now,
            }
          : c
      )
    );
    folderUpdatesRef.current.add(id);

    try {
      const checkRef = doc(db, 'checks', id);
      await updateDoc(checkRef, {
        folder: normalizedFolder,
        updatedAt: now,
      });

      folderUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(prevChecks => prevChecks.map(c => (c.id === id ? originalCheck : c)));
      folderUpdatesRef.current.delete(id);

      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot update check folder. Check user authentication and Firestore rules.');
      } else {
        log('Error updating check folder: ' + error.message);
      }
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  // Debounced folder update - batches rapid folder changes into single write
  // Used for drag-drop scenarios where multiple items may be moved quickly
  const flushPendingFolderUpdates = useCallback(async () => {
    const updates = new Map(pendingFolderUpdatesRef.current);
    pendingFolderUpdatesRef.current.clear();
    
    if (updates.size === 0) return;
    
    const now = Date.now();
    
    try {
      if (updates.size === 1) {
        // Single update - use updateDoc directly
        const [[id, folder]] = [...updates.entries()];
        await updateDoc(doc(db, 'checks', id), { folder, updatedAt: now });
      } else {
        // Multiple updates - batch them
        const batch = writeBatch(db);
        updates.forEach((folder, id) => {
          batch.update(doc(db, 'checks', id), { folder, updatedAt: now });
        });
        await batch.commit();
      }
      
      // Clear folder update tracking
      updates.forEach((_, id) => folderUpdatesRef.current.delete(id));
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic updates on failure
      setChecks(prevChecks => {
        const originalFolders = new Map<string, string | null>();
        prevChecks.forEach(c => {
          if (updates.has(c.id)) {
            originalFolders.set(c.id, c.folder ?? null);
          }
        });
        return prevChecks;
      });
      updates.forEach((_, id) => folderUpdatesRef.current.delete(id));
      log('Error updating folders: ' + error.message);
    }
  }, [invalidateCache, log]);

  const debouncedSetCheckFolder = useCallback((id: string, folder: string | null) => {
    if (!userId) throw new Error('Authentication required');

    const check = checks.find(w => w.id === id);
    if (!check) {
      throw new Error("Check not found");
    }

    const normalizedFolder = normalizeFolder(folder);
    const folderError = getFolderPathError(normalizedFolder);
    if (folderError) throw new Error(folderError);

    // Add to pending updates
    pendingFolderUpdatesRef.current.set(id, normalizedFolder);
    
    // Optimistic local update (immediate UI feedback)
    const now = Date.now();
    setChecks(prevChecks =>
      prevChecks.map(c =>
        c.id === id
          ? { ...c, folder: normalizedFolder, updatedAt: now }
          : c
      )
    );
    folderUpdatesRef.current.add(id);

    // Clear existing timer
    if (folderDebounceTimerRef.current) {
      clearTimeout(folderDebounceTimerRef.current);
    }

    // Set new timer to flush after debounce delay
    folderDebounceTimerRef.current = setTimeout(() => {
      flushPendingFolderUpdates();
    }, FOLDER_DEBOUNCE_DELAY);
    
    // Return a cleanup function that can be called to flush immediately
    return () => {
      if (folderDebounceTimerRef.current) {
        clearTimeout(folderDebounceTimerRef.current);
        folderDebounceTimerRef.current = null;
      }
      flushPendingFolderUpdates();
    };
  }, [userId, checks, flushPendingFolderUpdates]);

  // Folder feature: rename a folder (updates all checks whose folder is the folder or any descendant)
  const renameFolder = useCallback(async (fromFolder: string, toFolder: string) => {
    if (!userId) throw new Error('Authentication required');

    const from = normalizeFolder(fromFolder);
    const to = normalizeFolder(toFolder);
    if (!from) throw new Error('Source folder required');
    if (!to) throw new Error('Destination folder required');
    if (from === to) return;
    if (to.startsWith(from + '/')) {
      throw new Error('Destination folder cannot be inside the folder being renamed.');
    }
    const toError = getFolderPathError(to);
    if (toError) throw new Error(toError);

    const affected = checks.filter((c) => folderHasPrefix(c.folder, from));
    if (affected.length === 0) return;

    const now = Date.now();

    // Pre-validate the rewritten paths. Segment lengths survive a prefix
    // rename by construction, but depth can change (renaming "a" to "x/y"
    // pushes "a/b" to the 3-level "x/y/b").
    const nextFolderById = new Map<string, string | null>();
    for (const c of affected) {
      const current = normalizeFolder(c.folder);
      if (!current) continue;
      const next = replaceFolderPrefix(current, from, to);
      const nextError = getFolderPathError(next);
      if (nextError) {
        throw new Error(`Renaming would make some folders invalid: ${nextError}`);
      }
      nextFolderById.set(c.id, next);
    }

    const originalChecks = [...checks];

    // Optimistic update
    setChecks((prev) =>
      prev.map((c) => {
        const nextFolder = nextFolderById.get(c.id);
        if (nextFolder === undefined) return c;
        folderUpdatesRef.current.add(c.id);
        return { ...c, folder: nextFolder, updatedAt: now };
      })
    );

    try {
      // Firestore batches max 500 ops; chunk to stay safe
      const ids = [...nextFolderById.keys()];
      const chunkSize = 450;
      for (let i = 0; i < ids.length; i += chunkSize) {
        const slice = ids.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        for (const id of slice) {
          const folder = nextFolderById.get(id) ?? null;
          batch.update(doc(db, 'checks', id), { folder, updatedAt: now });
        }
        await batch.commit();
      }

      for (const id of nextFolderById.keys()) folderUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      setChecks(originalChecks);
      for (const id of nextFolderById.keys()) folderUpdatesRef.current.delete(id);
      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot rename folder. Check user authentication and Firestore rules.');
      } else {
        log('Error renaming folder: ' + error.message);
      }
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  // Folder feature: delete a folder (removes the segment and reparents descendants to the parent)
  const deleteFolder = useCallback(async (folderPath: string) => {
    if (!userId) throw new Error('Authentication required');

    const target = normalizeFolder(folderPath);
    if (!target) throw new Error('Folder required');

    const parent = (() => {
      const parts = target.split('/').filter(Boolean);
      if (parts.length <= 1) return null;
      return parts.slice(0, -1).join('/');
    })();

    const affected = checks.filter((c) => folderHasPrefix(c.folder, target));
    if (affected.length === 0) return;

    const now = Date.now();
    const nextFolderById = new Map<string, string | null>();

    for (const c of affected) {
      const current = normalizeFolder(c.folder);
      if (!current) continue;

      if (current === target) {
        nextFolderById.set(c.id, parent);
        continue;
      }

      // current starts with target + '/'
      const remainder = current.slice(target.length + 1);
      const next = parent ? `${parent}/${remainder}` : remainder;
      nextFolderById.set(c.id, next || null);
    }

    const originalChecks = [...checks];

    // Optimistic update
    setChecks((prev) =>
      prev.map((c) => {
        const nextFolder = nextFolderById.get(c.id);
        if (nextFolder === undefined) return c;
        folderUpdatesRef.current.add(c.id);
        return { ...c, folder: nextFolder, updatedAt: now };
      })
    );

    try {
      const ids = [...nextFolderById.keys()];
      const chunkSize = 450;
      for (let i = 0; i < ids.length; i += chunkSize) {
        const slice = ids.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        for (const id of slice) {
          const folder = nextFolderById.get(id) ?? null;
          batch.update(doc(db, 'checks', id), { folder, updatedAt: now });
        }
        await batch.commit();
      }

      for (const id of nextFolderById.keys()) folderUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      setChecks(originalChecks);
      for (const id of nextFolderById.keys()) folderUpdatesRef.current.delete(id);
      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot delete folder. Check user authentication and Firestore rules.');
      } else {
        log('Error deleting folder: ' + error.message);
      }
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  const deleteCheck = useCallback(async (id: string) => {
    if (!userId) throw new Error('Authentication required');

    // Check if check exists and belongs to user
    const check = checks.find(w => w.id === id);
    if (!check) {
      throw new Error("Check not found");
    }

    // Store original state for rollback
    const originalChecks = [...checks];

    // Optimistically remove from local state
    setChecks(prevChecks => prevChecks.filter(c => c.id !== id));
    optimisticUpdatesRef.current.add(id);
    pendingDeletesRef.current.add(id);

    try {
      // Use the deleteWebsite callable to properly update user_check_stats
      const result = await apiClient.deleteWebsite(id);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete check');
      }

      // Remove from optimistic updates and invalidate cache
      optimisticUpdatesRef.current.delete(id);
      pendingDeletesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(originalChecks);
      optimisticUpdatesRef.current.delete(id);
      pendingDeletesRef.current.delete(id);

      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot delete check. Check user authentication and Firestore rules.');
      } else {
        log('Error deleting check: ' + error.message);
      }
      throw error; // Re-throw to be caught by the caller
    }
  }, [userId, checks, invalidateCache, log]);

  // Ref to track state before drag started (for rollback on error)
  const reorderSnapshotRef = useRef<Website[] | null>(null);

  // Local-only reorder: updates UI instantly without any Firestore write.
  // Call this on dragOver for smooth visual feedback.
  const reorderChecksLocal = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    // Guard: prevent onSnapshot from overwriting mid-drag state
    isDraggingRef.current = true;
    pendingSnapshotRef.current = null;

    setChecks(prev => {
      const sorted = [...prev].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
      const movedCheck = sorted[fromIndex];
      if (!movedCheck) return prev;

      // Capture snapshot before first move of this drag
      if (!reorderSnapshotRef.current) {
        reorderSnapshotRef.current = prev;
      }

      const newOrder = [...sorted];
      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedCheck);

      // Assign sequential orderIndex for display only (not persisted yet)
      return newOrder.map((check, i) => ({ ...check, orderIndex: i * ORDER_INDEX_GAP }));
    });
  }, []);

  // Flush any Firestore snapshots that arrived while dragging was in progress.
  const flushPendingSnapshot = useCallback(() => {
    isDraggingRef.current = false;
    const pending = pendingSnapshotRef.current;
    pendingSnapshotRef.current = null;
    if (pending) {
      setChecks(pending);
    }
  }, []);

  // Persist the current local order to Firestore. Call once on drop/dragEnd.
  const commitReorder = useCallback(async () => {
    if (!userId) {
      flushPendingSnapshot();
      throw new Error('Authentication required');
    }

    const snapshot = reorderSnapshotRef.current;
    reorderSnapshotRef.current = null;
    if (!snapshot) {
      flushPendingSnapshot();
      return; // No drag happened
    }

    const currentChecks = [...checks].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
    const originalSorted = [...snapshot].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

    // Find which checks actually changed position
    const updatesToWrite: Array<{ id: string; orderIndex: number }> = [];
    for (let i = 0; i < currentChecks.length; i++) {
      const curr = currentChecks[i];
      const orig = originalSorted.find(c => c.id === curr.id);
      const newOrderIndex = i * ORDER_INDEX_GAP;
      if (!orig || (orig.orderIndex || 0) !== newOrderIndex) {
        updatesToWrite.push({ id: curr.id, orderIndex: newOrderIndex });
        optimisticUpdatesRef.current.add(curr.id);
      }
    }

    if (updatesToWrite.length === 0) {
      flushPendingSnapshot();
      return; // Nothing changed
    }

    try {
      if (updatesToWrite.length === 1) {
        const { id, orderIndex } = updatesToWrite[0];
        await updateDoc(doc(db, 'checks', id), { orderIndex });
      } else {
        const batch = writeBatch(db);
        updatesToWrite.forEach(({ id, orderIndex }) => {
          batch.update(doc(db, 'checks', id), { orderIndex });
        });
        await batch.commit();
      }

      updatesToWrite.forEach(({ id }) => optimisticUpdatesRef.current.delete(id));
      invalidateCache();
    } catch (error: any) {
      // Revert to pre-drag state on failure
      setChecks(snapshot);
      updatesToWrite.forEach(({ id }) => optimisticUpdatesRef.current.delete(id));

      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot reorder checks. Check user authentication and Firestore rules.');
      } else {
        log('Error reordering checks: ' + error.message);
      }
      throw error;
    } finally {
      flushPendingSnapshot();
    }
  }, [userId, checks, invalidateCache, log, flushPendingSnapshot]);

  // Revert local check order to the pre-drag snapshot and clear it.
  // Call this when a drag is cancelled (e.g. Escape) so: (a) the visual order
  // snaps back immediately without waiting for a Firestore update, and (b) the
  // snapshot ref is cleared so the next drag captures a fresh baseline.
  const rollbackReorder = useCallback(() => {
    const snapshot = reorderSnapshotRef.current;
    reorderSnapshotRef.current = null;
    // Prefer any Firestore data that arrived during drag, else revert to pre-drag snapshot
    const pending = pendingSnapshotRef.current;
    pendingSnapshotRef.current = null;
    isDraggingRef.current = false;
    if (pending) {
      setChecks(pending);
    } else if (snapshot) {
      setChecks(snapshot);
    }
  }, []);

  // Combined reorder + commit: reorders by check IDs and persists in one step.
  // Used by @dnd-kit onDragEnd — avoids stale closure issues with separate
  // reorderChecksLocal + commitReorder calls.
  const reorderAndCommit = useCallback(async (activeId: string, overId: string) => {
    if (!userId) throw new Error('Authentication required');
    if (activeId === overId) return;

    const sorted = [...checks].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
    const fromIndex = sorted.findIndex(c => c.id === activeId);
    const toIndex = sorted.findIndex(c => c.id === overId);
    if (fromIndex === -1 || toIndex === -1) return;

    // Compute new order
    const newOrder = [...sorted];
    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, sorted[fromIndex]);
    const reordered = newOrder.map((check, i) => ({ ...check, orderIndex: i * ORDER_INDEX_GAP }));

    // Guard Firestore listener and update UI immediately
    isDraggingRef.current = true;
    pendingSnapshotRef.current = null;
    setChecks(reordered);

    // Compute diffs
    const updatesToWrite: Array<{ id: string; orderIndex: number }> = [];
    for (let i = 0; i < reordered.length; i++) {
      const orig = sorted.find(c => c.id === reordered[i].id);
      const newOrderIndex = i * ORDER_INDEX_GAP;
      if (!orig || (orig.orderIndex || 0) !== newOrderIndex) {
        updatesToWrite.push({ id: reordered[i].id, orderIndex: newOrderIndex });
        optimisticUpdatesRef.current.add(reordered[i].id);
      }
    }

    if (updatesToWrite.length === 0) {
      flushPendingSnapshot();
      return;
    }

    try {
      if (updatesToWrite.length === 1) {
        const { id, orderIndex } = updatesToWrite[0];
        await updateDoc(doc(db, 'checks', id), { orderIndex });
      } else {
        const batch = writeBatch(db);
        updatesToWrite.forEach(({ id, orderIndex }) => {
          batch.update(doc(db, 'checks', id), { orderIndex });
        });
        await batch.commit();
      }

      updatesToWrite.forEach(({ id }) => optimisticUpdatesRef.current.delete(id));
      invalidateCache();
    } catch (error: any) {
      // Revert to pre-reorder state
      setChecks(sorted.map((c, i) => ({ ...c, orderIndex: i * ORDER_INDEX_GAP })));
      updatesToWrite.forEach(({ id }) => optimisticUpdatesRef.current.delete(id));
      log('Error reordering checks: ' + error.message);
      throw error;
    } finally {
      flushPendingSnapshot();
    }
  }, [userId, checks, invalidateCache, log, flushPendingSnapshot]);

  const toggleCheckStatus = useCallback(async (id: string, disabled: boolean) => {
    if (!userId) throw new Error('Authentication required');
    
    // Check if check exists and belongs to user
    const check = checks.find(w => w.id === id);
    if (!check) {
      throw new Error("Check not found");
    }
    
    const now = Date.now();
    
    // Store original state for rollback
    const originalCheck = { ...check };
    
    // Optimistically update local state
    setChecks(prevChecks => 
      prevChecks.map(c => 
        c.id === id 
          ? {
              ...c,
              disabled,
              disabledAt: disabled ? now : null,
              disabledReason: disabled ? "Manually disabled by user" : null,
              consecutiveFailures: disabled ? c.consecutiveFailures : 0,
              lastFailureTime: disabled ? c.lastFailureTime : null,
              updatedAt: now
            }
          : c
      )
    );
    optimisticUpdatesRef.current.add(id);
    
    try {
      // Always go through the callable so the VPS gets a `check_edits`
      // notification and disable-side effects (email, BigQuery history) run.
      const result = await apiClient.toggleWebsiteStatus({ id, disabled });
      if (!result.success) {
        throw new Error(result.error || (disabled ? 'Failed to disable check' : 'Failed to enable check'));
      }

      // Remove from optimistic updates and invalidate cache
      optimisticUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(prevChecks =>
        prevChecks.map(c => c.id === id ? originalCheck : c)
      );
      optimisticUpdatesRef.current.delete(id);

      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot toggle check status. Check user authentication and Firestore rules.');
      } else {
        log('Error toggling check status: ' + error.message);
      }
      throw error; // Re-throw to be caught by the caller
    }
  }, [userId, checks, invalidateCache, log]);

  const bulkDeleteChecks = useCallback(async (ids: string[]) => {
    if (!userId) throw new Error('Authentication required');

    // Verify all checks exist and belong to user
    const checksToDelete = checks.filter(w => ids.includes(w.id));
    if (checksToDelete.length !== ids.length) {
      throw new Error("Some checks not found or don't belong to you");
    }

    // Store original state for rollback
    const originalChecks = [...checks];

    // Optimistically remove from local state
    setChecks(prevChecks => prevChecks.filter(c => !ids.includes(c.id)));
    ids.forEach(id => optimisticUpdatesRef.current.add(id));
    ids.forEach(id => pendingDeletesRef.current.add(id));

    try {
      // Use the deleteWebsite callable for each check to properly update user_check_stats
      const results = await Promise.allSettled(
        ids.map(id => apiClient.deleteWebsite(id))
      );

      const failures = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success));
      if (failures.length > 0) {
        throw new Error(`Failed to delete ${failures.length} of ${ids.length} checks`);
      }

      // Remove from optimistic updates and invalidate cache
      ids.forEach(id => optimisticUpdatesRef.current.delete(id));
      ids.forEach(id => pendingDeletesRef.current.delete(id));
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(originalChecks);
      ids.forEach(id => optimisticUpdatesRef.current.delete(id));
      ids.forEach(id => pendingDeletesRef.current.delete(id));

      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot delete checks. Check user authentication and Firestore rules.');
      } else {
        log('Error deleting checks: ' + error.message);
      }
      throw error;
    }
  }, [userId, checks, log, invalidateCache]);

  const bulkToggleCheckStatus = useCallback(async (ids: string[], disabled: boolean) => {
    if (!userId) throw new Error('Authentication required');
    
    // Verify all checks exist and belong to user
    const checksToUpdate = checks.filter(w => ids.includes(w.id));
    if (checksToUpdate.length !== ids.length) {
      throw new Error("Some checks not found or don't belong to you");
    }
    
    const now = Date.now();
    
    // Store original state for rollback
    const originalChecks = [...checks];
    
    // Optimistically update local state
    setChecks(prevChecks => 
      prevChecks.map(c => 
        ids.includes(c.id)
          ? {
              ...c,
              disabled,
              disabledAt: disabled ? now : null,
              disabledReason: disabled ? "Bulk disabled by user" : null,
              consecutiveFailures: disabled ? c.consecutiveFailures : 0,
              lastFailureTime: disabled ? c.lastFailureTime : null,
              updatedAt: now
            }
          : c
      )
    );
    ids.forEach(id => optimisticUpdatesRef.current.add(id));
    
    try {
      // Always go through the callable so the VPS gets a `check_edits`
      // notification and disable-side effects (email, BigQuery history) run.
      const results = await Promise.allSettled(
        ids.map(id => apiClient.toggleWebsiteStatus({ id, disabled }))
      );
      const failures = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success));
      if (failures.length > 0) {
        const firstError = failures[0];
        const msg = firstError.status === 'rejected'
          ? firstError.reason?.message
          : (firstError.status === 'fulfilled' ? firstError.value.error : 'Unknown error');
        throw new Error(msg || `Failed to ${disabled ? 'disable' : 'enable'} ${failures.length} check(s)`);
      }

      // Remove from optimistic updates and invalidate cache
      ids.forEach(id => optimisticUpdatesRef.current.delete(id));
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(originalChecks);
      ids.forEach(id => optimisticUpdatesRef.current.delete(id));

      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot update checks. Check user authentication and Firestore rules.');
      } else {
        log('Error updating checks: ' + error.message);
      }
      throw error;
    }
  }, [userId, checks, log, invalidateCache]);

  // Bulk update settings for multiple checks
  const bulkUpdateSettings = useCallback(async (
    ids: string[],
    settings: {
      checkFrequency?: number;
      immediateRecheckEnabled?: boolean;
      downConfirmationAttempts?: number;
      expectedStatusCodes?: number[];
      checkRegionOverride?: 'us-central1' | 'europe-west1' | 'asia-southeast1' | 'vps-eu-1' | 'vps-us-1' | null;
      timezone?: string | null;
      domainAlertThresholds?: number[];
      severity?: 1 | 2 | 3 | 4 | 5 | null;
      public?: boolean;
    }
  ) => {
    if (!userId) throw new Error('Authentication required');
    if (ids.length === 0) return;
    if (Object.keys(settings).length === 0) return;

    // Verify all checks exist and belong to user
    const checksToUpdate = checks.filter(w => ids.includes(w.id));
    if (checksToUpdate.length !== ids.length) {
      throw new Error("One or more checks not found");
    }

    const now = Date.now();

    // Store original state for rollback
    const originalChecks = [...checks];

    // Optimistically update local state
    setChecks(prevChecks =>
      prevChecks.map(c =>
        ids.includes(c.id)
          ? {
              ...c,
              ...(settings.checkFrequency !== undefined && { checkFrequency: settings.checkFrequency }),
              ...(settings.immediateRecheckEnabled !== undefined && { immediateRecheckEnabled: settings.immediateRecheckEnabled }),
              ...(settings.downConfirmationAttempts !== undefined && { downConfirmationAttempts: settings.downConfirmationAttempts }),
              ...(settings.expectedStatusCodes !== undefined && { expectedStatusCodes: settings.expectedStatusCodes }),
              ...(settings.checkRegionOverride !== undefined && { checkRegionOverride: settings.checkRegionOverride }),
              ...(settings.checkRegionOverride && { checkRegion: settings.checkRegionOverride }),
              ...(settings.timezone !== undefined && { timezone: settings.timezone ?? undefined }),
              ...(settings.domainAlertThresholds !== undefined && c.domainExpiry?.enabled && {
                domainExpiry: { ...c.domainExpiry, alertThresholds: settings.domainAlertThresholds }
              }),
              ...(settings.severity !== undefined && { severity: settings.severity }),
              ...(settings.public !== undefined && { public: settings.public }),
              // Reset nextCheckAt when frequency or region changes so the correct scheduler picks it up
              ...((settings.checkFrequency !== undefined || settings.checkRegionOverride) && { nextCheckAt: now }),
              updatedAt: now
            }
          : c
      )
    );
    ids.forEach(id => optimisticUpdatesRef.current.add(id));

    // Batch update Firestore
    const batch = writeBatch(db);
    ids.forEach(id => {
      const docRef = doc(db, 'checks', id);
      batch.update(docRef, {
        ...(settings.checkFrequency !== undefined && { checkFrequency: settings.checkFrequency }),
        ...(settings.immediateRecheckEnabled !== undefined && { immediateRecheckEnabled: settings.immediateRecheckEnabled }),
        ...(settings.downConfirmationAttempts !== undefined && { downConfirmationAttempts: settings.downConfirmationAttempts }),
        ...(settings.expectedStatusCodes !== undefined && { expectedStatusCodes: settings.expectedStatusCodes }),
        ...(settings.checkRegionOverride !== undefined && { checkRegionOverride: settings.checkRegionOverride }),
        ...(settings.checkRegionOverride && { checkRegion: settings.checkRegionOverride }),
        ...(settings.timezone !== undefined && { timezone: settings.timezone }),
        ...(settings.domainAlertThresholds !== undefined && checksToUpdate.find(c => c.id === id)?.domainExpiry?.enabled && {
          'domainExpiry.alertThresholds': settings.domainAlertThresholds
        }),
        ...(settings.severity !== undefined && { severity: settings.severity }),
        ...(settings.public !== undefined && { public: settings.public }),
        // Reset nextCheckAt when frequency or region changes so the correct scheduler picks it up
        ...((settings.checkFrequency !== undefined || settings.checkRegionOverride) && { nextCheckAt: now }),
        updatedAt: now
      });
    });

    try {
      await batch.commit();

      // Remove from optimistic updates and invalidate cache
      ids.forEach(id => optimisticUpdatesRef.current.delete(id));
      invalidateCache();
      log(`Bulk updated settings for ${ids.length} check(s)`);
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(originalChecks);
      ids.forEach(id => optimisticUpdatesRef.current.delete(id));

      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot update checks. Check user authentication and Firestore rules.');
      } else {
        log('Error updating checks: ' + error.message);
      }
      throw error;
    }
  }, [userId, checks, log, invalidateCache]);

  const bulkMoveToFolder = useCallback(async (ids: string[], folder: string | null): Promise<Array<{ id: string; folder: string | null }>> => {
    if (!userId) throw new Error('Authentication required');
    if (ids.length === 0) return [];

    // Normalize + validate the folder value
    const normalizedFolder = normalizeFolder(folder);
    const folderError = getFolderPathError(normalizedFolder);
    if (folderError) throw new Error(folderError);

    // Verify all checks exist
    const checksToUpdate = checks.filter(w => ids.includes(w.id));
    if (checksToUpdate.length !== ids.length) {
      throw new Error("One or more checks not found");
    }

    // Captured before the write so the caller can offer Undo — folders are
    // derived from these strings, so a bad mass move erases the folder tree.
    const previousFolders = checksToUpdate.map(c => ({ id: c.id, folder: c.folder ?? null }));

    const now = Date.now();
    const originalChecks = [...checks];

    // Optimistic local update
    setChecks(prevChecks =>
      prevChecks.map(c =>
        ids.includes(c.id)
          ? { ...c, folder: normalizedFolder, updatedAt: now }
          : c
      )
    );
    ids.forEach(id => folderUpdatesRef.current.add(id));

    // Batch update Firestore
    const batch = writeBatch(db);
    ids.forEach(id => {
      const docRef = doc(db, 'checks', id);
      batch.update(docRef, {
        folder: normalizedFolder,
        updatedAt: now,
      });
    });

    try {
      await batch.commit();
      ids.forEach(id => folderUpdatesRef.current.delete(id));
      invalidateCache();
      log(`Bulk moved ${ids.length} check(s) to folder: ${normalizedFolder ?? '(none)'}`);
      return previousFolders;
    } catch (error: any) {
      // Rollback
      setChecks(originalChecks);
      ids.forEach(id => folderUpdatesRef.current.delete(id));
      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot move checks to folder.');
      } else {
        log('Error moving checks to folder: ' + error.message);
      }
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  // Restores per-check folder values captured by bulkMoveToFolder — the Undo
  // side of a bulk move. Checks deleted since the move are skipped.
  const restoreCheckFolders = useCallback(async (entries: Array<{ id: string; folder: string | null }>) => {
    if (!userId) throw new Error('Authentication required');
    const existingIds = new Set(checks.map(c => c.id));
    const toRestore = entries.filter(e => existingIds.has(e.id));
    if (toRestore.length === 0) return;

    const now = Date.now();
    const originalChecks = [...checks];
    const folderById = new Map(toRestore.map(e => [e.id, e.folder]));

    setChecks(prevChecks =>
      prevChecks.map(c =>
        folderById.has(c.id)
          ? { ...c, folder: folderById.get(c.id) ?? null, updatedAt: now }
          : c
      )
    );
    toRestore.forEach(e => folderUpdatesRef.current.add(e.id));

    const batch = writeBatch(db);
    toRestore.forEach(e => {
      batch.update(doc(db, 'checks', e.id), {
        folder: e.folder,
        updatedAt: now,
      });
    });

    try {
      await batch.commit();
      toRestore.forEach(e => folderUpdatesRef.current.delete(e.id));
      invalidateCache();
      log(`Restored folders for ${toRestore.length} check(s)`);
    } catch (error: any) {
      setChecks(originalChecks);
      toRestore.forEach(e => folderUpdatesRef.current.delete(e.id));
      log('Error restoring folders: ' + error.message);
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  // Manual check function with optimistic updates
  const manualCheck = useCallback(async (id: string) => {
    if (!userId) throw new Error('Authentication required');
    
    // Check if check exists and belongs to user
    const check = checks.find(w => w.id === id);
    if (!check) {
      throw new Error("Check not found");
    }
    
    const now = Date.now();
    
    // Store original state for rollback
    const originalCheck = { ...check };
    
    // Optimistically update local state - show that check is in progress
    setChecks(prevChecks => 
      prevChecks.map(c => 
        c.id === id 
          ? {
              ...c,
              lastChecked: now,
              status: 'unknown' as const, // Reset to unknown while checking
              updatedAt: now
            }
          : c
      )
    );
    optimisticUpdatesRef.current.add(id);
    manualChecksInProgressRef.current.add(id);
    
    try {
      // Call the manual check API
      const result = await apiClient.manualCheck(id);
      
      if (result.success && result.data) {
        // Update with the actual result
        setChecks(prevChecks => 
          prevChecks.map(c => 
            c.id === id 
              ? {
                  ...c,
                  lastChecked: result.data!.lastChecked,
                  status: result.data!.status as Website['status'], // Type assertion for API response
                  updatedAt: now
                }
              : c
          )
        );
      } else {
        // If API call failed, revert to original state
        setChecks(prevChecks => 
          prevChecks.map(c => c.id === id ? originalCheck : c)
        );
        throw new Error(result.error || 'Manual check failed');
      }
      
      // Remove from optimistic updates and invalidate cache
      optimisticUpdatesRef.current.delete(id);
      manualChecksInProgressRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      // Revert optimistic update on failure
      setChecks(prevChecks => 
        prevChecks.map(c => c.id === id ? originalCheck : c)
      );
      optimisticUpdatesRef.current.delete(id);
      manualChecksInProgressRef.current.delete(id);
      
      if (error.code === 'permission-denied') {
        log('Permission denied: Cannot perform manual check. Check user authentication and Firestore rules.');
      } else {
        log('Error performing manual check: ' + error.message);
      }
      throw error; // Re-throw to be caught by the caller
    }
  }, [userId, checks, invalidateCache, log]);

  const toggleMaintenanceMode = useCallback(async (
    id: string,
    enabled: boolean,
    duration?: number,
    reason?: string
  ) => {
    if (!userId) throw new Error('Authentication required');

    const check = checks.find(w => w.id === id);
    if (!check) throw new Error("Check not found");

    const now = Date.now();
    const originalCheck = { ...check };

    // Optimistic update
    setChecks(prevChecks =>
      prevChecks.map(c =>
        c.id === id
          ? enabled
            ? {
                ...c,
                maintenanceMode: true,
                maintenanceStartedAt: now,
                maintenanceExpiresAt: now + (duration || 0),
                maintenanceDuration: duration || 0,
                maintenanceReason: reason || undefined,
                updatedAt: now,
              }
            : {
                ...c,
                maintenanceMode: false,
                maintenanceStartedAt: undefined,
                maintenanceExpiresAt: undefined,
                maintenanceDuration: undefined,
                maintenanceReason: undefined,
                updatedAt: now,
              }
          : c
      )
    );
    optimisticUpdatesRef.current.add(id);

    try {
      const result = await apiClient.toggleMaintenanceMode({
        checkId: id,
        enabled,
        ...(enabled && duration ? { duration } : {}),
        ...(enabled && reason ? { reason } : {}),
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to toggle maintenance mode');
      }

      optimisticUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      // Rollback
      setChecks(prevChecks =>
        prevChecks.map(c => c.id === id ? originalCheck : c)
      );
      optimisticUpdatesRef.current.delete(id);
      log('Error toggling maintenance mode: ' + error.message);
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  const scheduleMaintenanceWindow = useCallback(async (
    id: string,
    startTime: number,
    duration: number,
    reason?: string
  ) => {
    if (!userId) throw new Error('Authentication required');
    const check = checks.find(w => w.id === id);
    if (!check) throw new Error("Check not found");
    const originalCheck = { ...check };

    setChecks(prev => prev.map(c => c.id === id ? {
      ...c,
      maintenanceScheduledStart: startTime,
      maintenanceScheduledDuration: duration,
      maintenanceScheduledReason: reason || null,
      updatedAt: Date.now(),
    } : c));
    optimisticUpdatesRef.current.add(id);

    try {
      const result = await apiClient.scheduleMaintenanceWindow({ checkId: id, startTime, duration, reason });
      if (!result.success) throw new Error(result.error || 'Failed to schedule maintenance');
      optimisticUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      setChecks(prev => prev.map(c => c.id === id ? originalCheck : c));
      optimisticUpdatesRef.current.delete(id);
      log('Error scheduling maintenance: ' + error.message);
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  const cancelScheduledMaintenance = useCallback(async (id: string) => {
    if (!userId) throw new Error('Authentication required');
    const check = checks.find(w => w.id === id);
    if (!check) throw new Error("Check not found");
    const originalCheck = { ...check };

    setChecks(prev => prev.map(c => c.id === id ? {
      ...c,
      maintenanceScheduledStart: null,
      maintenanceScheduledDuration: null,
      maintenanceScheduledReason: null,
      updatedAt: Date.now(),
    } : c));
    optimisticUpdatesRef.current.add(id);

    try {
      const result = await apiClient.cancelScheduledMaintenance({ checkId: id });
      if (!result.success) throw new Error(result.error || 'Failed to cancel scheduled maintenance');
      optimisticUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      setChecks(prev => prev.map(c => c.id === id ? originalCheck : c));
      optimisticUpdatesRef.current.delete(id);
      log('Error cancelling scheduled maintenance: ' + error.message);
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  const setRecurringMaintenance = useCallback(async (
    id: string,
    daysOfWeek: number[],
    startTimeMinutes: number,
    durationMinutes: number,
    timezone: string,
    reason?: string
  ) => {
    if (!userId) throw new Error('Authentication required');
    const check = checks.find(w => w.id === id);
    if (!check) throw new Error("Check not found");
    const originalCheck = { ...check };

    setChecks(prev => prev.map(c => c.id === id ? {
      ...c,
      maintenanceRecurring: { daysOfWeek, startTimeMinutes, durationMinutes, timezone, reason: reason || null, createdAt: Date.now() },
      maintenanceRecurringActiveUntil: null,
      updatedAt: Date.now(),
    } : c));
    optimisticUpdatesRef.current.add(id);

    try {
      const result = await apiClient.setRecurringMaintenance({ checkId: id, daysOfWeek, startTimeMinutes, durationMinutes, timezone, reason });
      if (!result.success) throw new Error(result.error || 'Failed to set recurring maintenance');
      optimisticUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      setChecks(prev => prev.map(c => c.id === id ? originalCheck : c));
      optimisticUpdatesRef.current.delete(id);
      log('Error setting recurring maintenance: ' + error.message);
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  const deleteRecurringMaintenance = useCallback(async (id: string) => {
    if (!userId) throw new Error('Authentication required');
    const check = checks.find(w => w.id === id);
    if (!check) throw new Error("Check not found");
    const originalCheck = { ...check };

    setChecks(prev => prev.map(c => c.id === id ? {
      ...c,
      maintenanceRecurring: null,
      maintenanceRecurringActiveUntil: null,
      updatedAt: Date.now(),
    } : c));
    optimisticUpdatesRef.current.add(id);

    try {
      const result = await apiClient.deleteRecurringMaintenance({ checkId: id });
      if (!result.success) throw new Error(result.error || 'Failed to delete recurring maintenance');
      optimisticUpdatesRef.current.delete(id);
      invalidateCache();
    } catch (error: any) {
      setChecks(prev => prev.map(c => c.id === id ? originalCheck : c));
      optimisticUpdatesRef.current.delete(id);
      log('Error deleting recurring maintenance: ' + error.message);
      throw error;
    }
  }, [userId, checks, invalidateCache, log]);

  // Manual refresh function - call this after adding/updating checks to update the UI immediately
  const refresh = useCallback(() => {
    // No-op with realtime subscription, kept for API compatibility
    invalidateCache();
  }, [invalidateCache]);

  return {
    checks,
    loading,
    addCheck,
    updateCheck,
    deleteCheck,
    bulkDeleteChecks,
    reorderChecksLocal,
    commitReorder,
    rollbackReorder,
    reorderAndCommit,
    toggleCheckStatus,
    toggleMaintenanceMode,
    scheduleMaintenanceWindow,
    cancelScheduledMaintenance,
    setRecurringMaintenance,
    deleteRecurringMaintenance,
    bulkToggleCheckStatus,
    bulkUpdateSettings, // Bulk update settings for multiple checks
    bulkMoveToFolder, // Bulk move checks to a folder (returns previous folders for undo)
    restoreCheckFolders, // Undo a bulk folder move
    manualCheck, // Expose manual check function
    setCheckFolder, // Expose folder mutation (Nano feature)
    debouncedSetCheckFolder, // Debounced version for drag-drop (batches writes)
    flushPendingFolderUpdates, // Flush pending folder updates immediately
    renameFolder, // Expose folder rename (Nano feature)
    deleteFolder, // Expose folder delete (Nano feature)
    refresh, // Expose refresh function for manual cache invalidation
    optimisticUpdates: Array.from(optimisticUpdatesRef.current), // Expose optimistic updates for UI feedback
    folderUpdates: Array.from(folderUpdatesRef.current), // Expose folder-only updates for UI control
    manualChecksInProgress: Array.from(manualChecksInProgressRef.current) // Expose manual checks in progress for UI feedback
  };
} 
